import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  listAdmins,
  inviteAdmin,
  changeMemberRole,
  removeMember,
  sendPasswordResetForMember,
  type BackendRole,
} from "@/lib/admin.functions";
import { adminUpdateStaffProfile } from "@/lib/staff.functions";
import { sendInviteReminder } from "@/lib/invite-reminder.functions";
import { useAdminSession } from "@/hooks/use-admin-session";
import { Trash2, KeyRound, Pencil, X, Loader2, Upload, User as UserIcon, Briefcase, Phone, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { confirmDialog } from "@/lib/confirm-dialog";
import { getPersonImpact } from "@/lib/team-people.functions";
import { SHARED_PROFILE_NOTICE } from "@/lib/team-people";

export const Route = createFileRoute("/_admin/admin/team")({
  component: TeamPage,
});

const ROLE_LABEL: Record<BackendRole, string> = {
  admin: "Administrator",
  editor: "Editor",
};

const STAFF_BUCKET = "staff-photos";
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp";

type Member = {
  id: string;
  email: string | null;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  jobTitle: string | null;
  bio: string | null;
  photoPath: string | null;
  photoUrl: string | null;
  isPublic: boolean;
  publicSortOrder: number;
  role: BackendRole;
  createdAt: string;
};

function TeamPage() {
  const session = useAdminSession();
  const listFn = useServerFn(listAdmins);
  const inviteFn = useServerFn(inviteAdmin);
  const changeRoleFn = useServerFn(changeMemberRole);
  const removeFn = useServerFn(removeMember);
  const sendResetFn = useServerFn(sendPasswordResetForMember);
  const personImpact = useServerFn(getPersonImpact);
  const reminderFn = useServerFn(sendInviteReminder);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-team"],
    queryFn: () => listFn(),
    enabled: session.isAdmin,
  });

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<BackendRole>("editor");
  const [editing, setEditing] = useState<Member | null>(null);

  const inviteMutation = useMutation({
    mutationFn: (input: {
      email: string;
      firstName?: string;
      lastName?: string;
      fullName?: string;
      role: BackendRole;
      origin: string;
    }) => inviteFn({ data: input }),
    onSuccess: (result) => {
      toast.success(
        result?.reinvited ? "Einladung erneut versendet." : "Einladung verschickt.",
      );
      setEmail("");
      setFirstName("");
      setLastName("");
      setRole("editor");
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
    },
    onError: (err: Error) => toast.error(err.message || "Einladung fehlgeschlagen."),
  });

  const changeRoleMutation = useMutation({
    mutationFn: (input: { userId: string; role: BackendRole }) => changeRoleFn({ data: input }),
    onSuccess: () => {
      toast.success("Rolle aktualisiert.");
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
    },
    onError: (err: Error) => toast.error(err.message || "Rollenwechsel fehlgeschlagen."),
  });

  const removeMutation = useMutation({
    mutationFn: (input: { userId: string }) => removeFn({ data: input }),
    onSuccess: () => {
      toast.success("Mitglied entfernt.");
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
    },
    onError: (err: Error) => toast.error(err.message || "Entfernen fehlgeschlagen."),
  });

  const reminderMutation = useMutation({
    mutationFn: (input: { email: string; origin: string }) => reminderFn({ data: input }),
    onSuccess: (res) => {
      if (res.sent) toast.success(res.message);
      else toast.error(res.message);
    },
    onError: (err: Error) => toast.error(err.message || "Versand fehlgeschlagen."),
  });

  const sendResetMutation = useMutation({

    mutationFn: (input: { userId: string; origin: string }) => sendResetFn({ data: input }),
    onSuccess: (result) => {
      toast.success(
        result?.flow === "invite"
          ? "Link zum Passwort setzen verschickt."
          : "Link zum Zurücksetzen verschickt.",
      );
    },
    onError: (err: Error) => toast.error(err.message || "Versand fehlgeschlagen."),
  });

  if (session.status === "authenticated" && !session.isAdmin) {
    return (
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-extrabold md:text-4xl">Team</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Sie haben keine Berechtigung, das Team zu verwalten.
        </p>
      </div>
    );
  }

  const trimmedEmail = email.trim().toLowerCase();
  const domainInvalid = trimmedEmail.includes("@") && !trimmedEmail.endsWith("@creategy.de");

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!trimmedEmail.endsWith("@creategy.de")) {
      toast.error("Bitte eine gültige @creategy.de-Adresse eintragen.");
      return;
    }
    const fn = firstName.trim();
    const ln = lastName.trim();
    inviteMutation.mutate({
      email: email.trim(),
      firstName: fn || undefined,
      lastName: ln || undefined,
      fullName: [fn, ln].filter(Boolean).join(" ") || undefined,
      role,
      origin: window.location.origin,
    });
  }

  const admins = (data?.admins ?? []) as Member[];
  const adminCount = admins.filter((a) => a.role === "admin").length;

  return (
    <div className="max-w-6xl">
      <h1 className="font-display text-3xl font-extrabold md:text-4xl">Team</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Laden Sie weitere Personen ins Backend ein. Administratoren dürfen alles ändern,
        Editoren pflegen Inhalte und ihr eigenes Profil.
      </p>

      <section className="mt-8 rounded-lg border border-border bg-background p-5">
        <h2 className="font-display text-lg font-extrabold">Neues Teammitglied einladen</h2>
        <form
          onSubmit={onSubmit}
          className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1.4fr_auto_auto]"
        >
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vorname</label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={80} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nachname</label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={80} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">E-Mail *</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vorname.nachname@creategy.de" aria-invalid={domainInvalid || undefined} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground" />
            {domainInvalid && (
              <p className="mt-1 text-xs text-destructive">Nur @creategy.de-Adressen sind zulässig.</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rolle</label>
            <select value={role} onChange={(e) => setRole(e.target.value as BackendRole)} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground">
              <option value="editor">Editor</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={inviteMutation.isPending || domainInvalid}
              className="h-[38px] w-full rounded-md bg-brand-button px-4 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60 xl:w-auto"
            >
              {inviteMutation.isPending ? "Einladung läuft…" : "Einladen"}
            </button>
          </div>
        </form>
      </section>

      <section className="mt-12">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-xl font-extrabold">Aktives Team</h2>
          {admins.length > 0 && (
            <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
              {admins.length}
            </span>
          )}
        </div>

        <div className="mt-4 space-y-2">
          {isLoading && (
            <>
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-[76px] animate-pulse rounded-lg border border-border bg-secondary/40" />
              ))}
            </>
          )}
          {isError && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              Konnte Liste nicht laden.
            </div>
          )}
          {!isLoading && admins.length === 0 && (
            <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              Noch keine Mitglieder eingetragen.
            </div>
          )}
          {admins.map((a) => {
            const isSelf = a.id === session.userId;
            const isLastAdmin = a.role === "admin" && adminCount <= 1;
            const roleLocked = isSelf || isLastAdmin;
            const removeLocked = isSelf || isLastAdmin;
            const displayName =
              a.fullName ??
              [a.firstName, a.lastName].filter(Boolean).join(" ") ??
              a.email ??
              "—";
            return (
              <div
                key={a.id}
                className="rounded-lg border border-border bg-background p-4 transition-colors hover:border-foreground/20"
              >
                <div className="flex flex-wrap items-center gap-4">
                  {/* Person */}
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary">
                      {a.photoUrl ? (
                        <img src={a.photoUrl} alt={displayName} className="h-full w-full object-cover" />
                      ) : (
                        <UserIcon className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 truncate font-semibold text-primary">
                        <span className="truncate">{displayName}</span>
                        {isSelf && (
                          <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">Sie</span>
                        )}
                        {a.isPublic && (
                          <span
                            className="rounded bg-brand-button px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brand-button-foreground"
                            title={`Erscheint auf der Website (Reihenfolge ${a.publicSortOrder})`}
                          >
                            Website
                          </span>
                        )}
                      </p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        {a.email && (
                          <span className="inline-flex items-center gap-1 truncate">
                            <Mail className="h-3 w-3 shrink-0" />
                            <span className="truncate">{a.email}</span>
                          </span>
                        )}
                        {a.jobTitle && (
                          <span className="inline-flex items-center gap-1">
                            <Briefcase className="h-3 w-3 shrink-0" />
                            {a.jobTitle}
                          </span>
                        )}
                        {a.phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone className="h-3 w-3 shrink-0" />
                            {a.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Role + Actions */}
                  <div className="flex shrink-0 items-center gap-2">
                    <select
                      value={a.role}
                      disabled={roleLocked || changeRoleMutation.isPending}
                      onChange={(e) => changeRoleMutation.mutate({ userId: a.id, role: e.target.value as BackendRole })}
                      className="rounded-md border border-border bg-background px-2 py-1.5 text-xs disabled:opacity-60"
                      title={isSelf ? "Eigene Rolle kann nicht geändert werden" : isLastAdmin ? "Letzter Administrator" : undefined}
                    >
                      <option value="admin">{ROLE_LABEL.admin}</option>
                      <option value="editor">{ROLE_LABEL.editor}</option>
                    </select>

                    <button
                      type="button"
                      onClick={() => setEditing(a)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2 text-xs font-semibold text-foreground hover:bg-secondary"
                      title="Profil bearbeiten"
                      aria-label="Profil bearbeiten"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      <span className="hidden 2xl:inline">Bearbeiten</span>
                    </button>
                    <button
                      type="button"
                      disabled={sendResetMutation.isPending}
                      onClick={async () => {
                        if (
                          !(await confirmDialog({
                            title: `Link an ${displayName} senden?`,
                            description:
                              "Die Person erhält eine E-Mail, um ein neues Passwort zu setzen.",
                            confirmLabel: "Link senden",
                            destructive: false,
                          }))
                        )
                          return;
                        sendResetMutation.mutate({ userId: a.id, origin: window.location.origin });
                      }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2 text-xs font-semibold text-foreground hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                      title="Link zum Passwort setzen senden"
                      aria-label="Link zum Passwort setzen senden"
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                      <span className="hidden 2xl:inline">Link senden</span>
                    </button>
                    {a.email ? (
                      <button
                        type="button"
                        disabled={reminderMutation.isPending}
                        onClick={async () => {
                          if (
                            !(await confirmDialog({
                              title: `Erinnerung an ${displayName} senden?`,
                              description: "Die offene Einladung wird erneut zugestellt.",
                              confirmLabel: "Erinnerung senden",
                              destructive: false,
                            }))
                          )
                            return;
                          reminderMutation.mutate({
                            email: a.email as string,
                            origin: window.location.origin,
                          });
                        }}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2 text-xs font-semibold text-foreground hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                        title="Erinnerung an offene Einladung senden"
                        aria-label="Erinnerung an offene Einladung senden"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        <span className="hidden 2xl:inline">Erinnerung</span>
                      </button>
                    ) : null}

                    <button
                      type="button"
                      disabled={removeLocked || removeMutation.isPending}
                      onClick={async () => {
                        // Ticket W12, Punkt 4: Bestehende Zuordnungen sperren
                        // das Löschen. Lieber ein Klick mehr als eine
                        // Kursseite, die unbemerkt eine Person verliert.
                        let publicNote = "";
                        try {
                          const impact = await personImpact({ data: { profileId: a.id } });
                          if (impact && impact.offers.length > 0) {
                            toast.error(
                              `${displayName} erscheint noch auf: ${impact.offers.join(", ")}. Bitte entfernen Sie die Zuordnungen dort zuerst.`,
                              { duration: 10000 },
                            );
                            return;
                          }
                          if (impact?.isPublic) {
                            publicNote =
                              " Das Profil ist für die Website freigegeben; die Karte verschwindet damit von allen öffentlichen Seiten.";
                          }
                        } catch {
                          // Der Hinweis ist eine Hilfe, kein Hindernis – die
                          // Datenbanksperre greift ohnehin.
                        }
                        if (
                          !(await confirmDialog({
                            title: `${displayName} entfernen?`,
                            description:
                              "Die Person verliert sofort den Zugang zum internen Bereich." +
                              publicNote,
                            confirmLabel: "Entfernen",
                          }))
                        )
                          return;
                        removeMutation.mutate({ userId: a.id });
                      }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2 text-xs font-semibold text-foreground hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                      title={isSelf ? "Sie können sich nicht selbst entfernen" : isLastAdmin ? "Letzter Administrator" : "Entfernen"}
                      aria-label="Entfernen"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span className="hidden 2xl:inline">Entfernen</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {editing && (
        <EditDrawer
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ["admin-team"] });
          }}
        />
      )}
    </div>
  );
}

function EditDrawer({
  member,
  onClose,
  onSaved,
}: {
  member: Member;
  onClose: () => void;
  onSaved: () => void;
}) {
  const saveFn = useServerFn(adminUpdateStaffProfile);
  const [firstName, setFirstName] = useState(member.firstName ?? "");
  const [lastName, setLastName] = useState(member.lastName ?? "");
  const [phone, setPhone] = useState(member.phone ?? "");
  const [jobTitle, setJobTitle] = useState(member.jobTitle ?? "");
  const [bio, setBio] = useState(member.bio ?? "");
  const [isPublic, setIsPublic] = useState(member.isPublic);
  const [publicSortOrder, setPublicSortOrder] = useState(String(member.publicSortOrder ?? 0));
  const [photoPath, setPhotoPath] = useState<string | null>(member.photoPath);
  const [photoUrl, setPhotoUrl] = useState<string | null>(member.photoUrl);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: (input: {
      userId: string;
      first_name: string;
      last_name: string;
      phone: string | null;
      job_title: string | null;
      bio: string | null;
      photo_path: string | null;
      is_public: boolean;
      public_sort_order: number;
    }) => saveFn({ data: input }),
    onSuccess: () => {
      toast.success("Profil aktualisiert.");
      onSaved();
    },
    onError: (err: Error) => toast.error(err.message || "Speichern fehlgeschlagen."),
  });

  async function handleFile(file: File) {
    if (file.size > MAX_BYTES) {
      toast.error("Datei ist zu groß (max. 5 MB).");
      return;
    }
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Format nicht erlaubt.");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
      const path = `${member.id}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from(STAFF_BUCKET)
        .upload(path, file, { cacheControl: "3600", contentType: file.type || undefined, upsert: false });
      if (error) throw error;
      if (photoPath) await supabase.storage.from(STAFF_BUCKET).remove([photoPath]);
      const { data: signed } = await supabase.storage.from(STAFF_BUCKET).createSignedUrl(path, 60 * 15);
      setPhotoPath(path);
      setPhotoUrl(signed?.signedUrl ?? null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
      <button
        aria-label="Schließen"
        className="flex-1 bg-black/40"
        onClick={onClose}
      />
      <div className="flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">Profil bearbeiten</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-1 text-xs text-muted-foreground">{member.email}</p>

        <div className="mt-6 flex items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary">
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : photoUrl ? (
              <img src={photoUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <UserIcon className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={ACCEPT}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
                e.currentTarget.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
            >
              <Upload className="h-3.5 w-3.5" /> {photoPath ? "Foto ersetzen" : "Foto hochladen"}
            </button>
            {photoPath && (
              <button
                type="button"
                onClick={async () => {
                  if (photoPath) await supabase.storage.from(STAFF_BUCKET).remove([photoPath]);
                  setPhotoPath(null);
                  setPhotoUrl(null);
                }}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold text-destructive hover:bg-secondary"
              >
                <Trash2 className="h-3.5 w-3.5" /> Entfernen
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vorname *</span>
            <input required value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground" maxLength={80} />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nachname *</span>
            <input required value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground" maxLength={80} />
          </label>
          <label className="block sm:col-span-2">
            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Funktionsbezeichnung</span>
            <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground" maxLength={80} />
          </label>
          <label className="block sm:col-span-2">
            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Telefonnummer</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground" maxLength={40} />
          </label>
          <label className="block sm:col-span-2">
            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Kurzbio (für Blog-Autor:innen)</span>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Kurze Vorstellung — erscheint unter Blogbeiträgen dieser Person."
              className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
            />
            <span className="mt-1 block text-[11px] text-muted-foreground">{bio.length}/1000</span>
          </label>
        </div>

        {/* Ticket W12: Kennzeichen und Reihenfolge für die öffentliche Website */}
        <div className="mt-6 rounded-lg border border-border bg-secondary/30 p-4">
          <h3 className="font-display text-sm font-extrabold">Öffentliche Website</h3>
          <p className="mt-1 text-xs text-muted-foreground">{SHARED_PROFILE_NOTICE}</p>
          <label className="mt-3 flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              Auf der Website zeigen
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Ohne dieses Kennzeichen erscheint die Person nirgends öffentlich – ein Konto allein
                genügt nicht.
              </span>
            </span>
          </label>
          <label className="mt-3 block max-w-[10rem]">
            <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Reihenfolge
            </span>
            <input
              type="number"
              min={0}
              max={9999}
              value={publicSortOrder}
              onChange={(e) => setPublicSortOrder(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
            />
            <span className="mt-1 block text-[11px] text-muted-foreground">
              Kleinere Zahl erscheint weiter vorn.
            </span>
          </label>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={mutation.isPending || uploading || !firstName.trim() || !lastName.trim()}
            onClick={() =>
              mutation.mutate({
                userId: member.id,
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                phone: phone.trim() || null,
                job_title: jobTitle.trim() || null,
                bio: bio.trim() || null,
                photo_path: photoPath,
                is_public: isPublic,
                public_sort_order: Number(publicSortOrder) || 0,
              })
            }
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {mutation.isPending ? "Speichern …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
